import { RecruitGroupResponseDto } from './recruit-groups.dto';

const REVIEW_MARKER = '<!--shout-review:';
const REVIEW_END = '-->';

export const REVIEW_NOTE_MAX_LENGTH = 140;
export const REVIEWER_NAME_MAX_LENGTH = 40;

export type CreatorReviewStatus = 'approved' | 'declined' | 'feedback' | 'pending';
export type CreatorReviewFilter = 'all' | CreatorReviewStatus;

export const CREATOR_REVIEW_FILTERS: ReadonlyArray<{ id: CreatorReviewFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Needs review' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
  { id: 'feedback', label: 'Feedback' },
];

/** Reviewer share page only. Owner groups always start on All. */
export function defaultCreatorReviewFilter(pendingCount: number): CreatorReviewFilter {
  return pendingCount > 0 ? 'pending' : 'all';
}

export function creatorReviewFilterCounts(
  statuses: readonly CreatorReviewStatus[],
): Record<CreatorReviewFilter, number> {
  const counts: Record<CreatorReviewFilter, number> = {
    all: statuses.length,
    pending: 0,
    approved: 0,
    declined: 0,
    feedback: 0,
  };
  for (const status of statuses) {
    counts[status] += 1;
  }
  return counts;
}

export function resolveCreatorReviewStatus(input: {
  declined: boolean;
  approved: boolean;
  hasNote: boolean;
  groupApproved: boolean;
}): CreatorReviewStatus {
  if (input.declined) {
    return 'declined';
  }
  if (input.approved) {
    return 'approved';
  }
  if (input.hasNote) {
    return 'feedback';
  }
  if (input.groupApproved) {
    return 'approved';
  }
  return 'pending';
}

export function creatorReviewFilterEmptyCopy(filter: CreatorReviewFilter): { title: string; message: string } {
  switch (filter) {
    case 'pending':
      return {
        title: 'Nothing needs review',
        message: 'Every creator has been reviewed. Choose All to see everyone.',
      };
    case 'approved':
      return {
        title: 'No approved creators',
        message: 'Choose All to see everyone else.',
      };
    case 'declined':
      return {
        title: 'No declined creators',
        message: 'Choose All to see everyone else.',
      };
    case 'feedback':
      return {
        title: 'No feedback yet',
        message: 'Creators with notes will show here.',
      };
    default:
      return {
        title: 'No creators',
        message: 'Nobody has been added yet.',
      };
  }
}

export interface RecruitGroupReviewState {
  droppedCreatorIds: string[];
  droppedByName?: string;
  keptCreatorIds: string[];
  keptByName?: string;
  approvedByName?: string;
  creatorNotes: Record<string, string>;
  allowReviewerInvite?: boolean;
  reviewerInviteBriefId?: string;
  invitedCreatorIds?: string[];
}

export function visibleGroupDescription(description: string | undefined): string {
  if (!description) {
    return '';
  }
  const marked = description.lastIndexOf(`\n${REVIEW_MARKER}`);
  const idx = marked >= 0 ? marked : description.lastIndexOf(REVIEW_MARKER);
  if (idx === -1) {
    return description;
  }
  return description.slice(0, idx).trimEnd();
}

export function normalizeReviewNote(value: string | undefined): string {
  return (value ?? '')
    .replaceAll(REVIEW_MARKER, '')
    .replaceAll(REVIEW_END, '')
    .trim()
    .slice(0, REVIEW_NOTE_MAX_LENGTH);
}

export function normalizeReviewerFirstName(value: string | undefined): string {
  return (value ?? '')
    .replaceAll(REVIEW_MARKER, '')
    .replaceAll(REVIEW_END, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, REVIEWER_NAME_MAX_LENGTH);
}

export function parseCreatorNotes(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const notes: Record<string, string> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!id.trim() || typeof value !== 'string') {
      continue;
    }
    const note = normalizeReviewNote(value);
    if (note) {
      notes[id] = note;
    }
  }
  return notes;
}

export function hasRecruitGroupReview(review: RecruitGroupReviewState): boolean {
  return review.droppedCreatorIds.length > 0
    || review.keptCreatorIds.length > 0
    || !!review.droppedByName
    || !!review.keptByName
    || !!review.approvedByName
    || Object.keys(review.creatorNotes).length > 0
    || Boolean(review.allowReviewerInvite)
    || Boolean(review.reviewerInviteBriefId)
    || (review.invitedCreatorIds?.length ?? 0) > 0;
}

export function parseRecruitGroupReview(
  group: Pick<
    RecruitGroupResponseDto,
    | 'groupDescription'
    | 'droppedCreatorIds'
    | 'droppedByName'
    | 'keptCreatorIds'
    | 'keptByName'
    | 'approvedByName'
    | 'creatorNotes'
    | 'allowReviewerInvite'
    | 'reviewerInviteBriefId'
    | 'invitedCreatorIds'
  >,
): RecruitGroupReviewState {
  const fromDescription = parseEncodedReview(group.groupDescription);
  const explicitNotes = Object.prototype.hasOwnProperty.call(group, 'creatorNotes');

  return {
    droppedCreatorIds: group.droppedCreatorIds?.length
      ? group.droppedCreatorIds
      : fromDescription.droppedCreatorIds,
    droppedByName: group.droppedByName ?? fromDescription.droppedByName,
    keptCreatorIds: exclusiveKeptIds(
      group.keptCreatorIds?.length
        ? group.keptCreatorIds
        : fromDescription.keptCreatorIds,
      group.droppedCreatorIds?.length
        ? group.droppedCreatorIds
        : fromDescription.droppedCreatorIds,
    ),
    keptByName: group.keptByName ?? fromDescription.keptByName,
    approvedByName: group.approvedByName ?? fromDescription.approvedByName,
    creatorNotes: explicitNotes
      ? parseCreatorNotes(group.creatorNotes)
      : fromDescription.creatorNotes,
    allowReviewerInvite: Boolean(group.allowReviewerInvite) || Boolean(fromDescription.allowReviewerInvite),
    reviewerInviteBriefId: group.reviewerInviteBriefId || fromDescription.reviewerInviteBriefId,
    invitedCreatorIds: uniqueIds([
      ...(group.invitedCreatorIds ?? []),
      ...(fromDescription.invitedCreatorIds ?? []),
    ]),
  };
}

export function embedRecruitGroupReview(
  description: string | undefined,
  review: RecruitGroupReviewState,
): string {
  const visible = visibleGroupDescription(description);
  const payload = JSON.stringify({
    droppedCreatorIds: review.droppedCreatorIds,
    droppedByName: review.droppedByName,
    keptCreatorIds: review.keptCreatorIds,
    keptByName: review.keptByName,
    approvedByName: review.approvedByName,
    creatorNotes: review.creatorNotes,
    allowReviewerInvite: review.allowReviewerInvite,
    reviewerInviteBriefId: review.reviewerInviteBriefId,
    invitedCreatorIds: review.invitedCreatorIds ?? [],
  });
  return `${visible}\n${REVIEW_MARKER}${payload}${REVIEW_END}`.trim();
}

export function hydrateRecruitGroupReview(group: RecruitGroupResponseDto): RecruitGroupResponseDto {
  return { ...group, ...parseRecruitGroupReview(group) };
}

function parseIdList(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    : [];
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

function exclusiveKeptIds(keptCreatorIds: string[], droppedCreatorIds: string[]): string[] {
  const declined = new Set(droppedCreatorIds);
  return keptCreatorIds.filter((id) => !declined.has(id));
}

function parseEncodedReview(description: string | undefined): RecruitGroupReviewState {
  const empty: RecruitGroupReviewState = {
    droppedCreatorIds: [],
    keptCreatorIds: [],
    creatorNotes: {},
    invitedCreatorIds: [],
  };
  if (!description) {
    return empty;
  }
  const start = description.lastIndexOf(REVIEW_MARKER);
  if (start === -1) {
    return empty;
  }
  const jsonStart = start + REVIEW_MARKER.length;
  const end = description.indexOf(REVIEW_END, jsonStart);
  if (end === -1) {
    return empty;
  }

  try {
    const parsed = JSON.parse(description.slice(jsonStart, end)) as Partial<RecruitGroupReviewState>;
    return {
      droppedCreatorIds: parseIdList(parsed.droppedCreatorIds),
      droppedByName: typeof parsed.droppedByName === 'string' ? parsed.droppedByName : undefined,
      keptCreatorIds: parseIdList(parsed.keptCreatorIds),
      keptByName: typeof parsed.keptByName === 'string' ? parsed.keptByName : undefined,
      approvedByName: typeof parsed.approvedByName === 'string' ? parsed.approvedByName : undefined,
      creatorNotes: parseCreatorNotes(parsed.creatorNotes),
      allowReviewerInvite: parsed.allowReviewerInvite === true,
      reviewerInviteBriefId: typeof parsed.reviewerInviteBriefId === 'string' && parsed.reviewerInviteBriefId.trim()
        ? parsed.reviewerInviteBriefId
        : undefined,
      invitedCreatorIds: parseIdList(parsed.invitedCreatorIds),
    };
  } catch {
    return empty;
  }
}
